import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { HiOutlinePlus, HiOutlineX, HiOutlineCheck, HiOutlineTrash } from 'react-icons/hi';
import bookService from '../../../services/book';
import toastService from '../../../services/toast';
import axiosInstance from '../../../config/axios';
import FormModal from '../../common/FormModal';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const modules = {
  toolbar: [
    [{ size: ['small', false, 'large', 'huge'] }],
    ['bold', 'italic', 'underline'],
    [{ color: [] }],
    [{ align: ['right', 'center'] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['blockquote', 'link'],
    ['clean']
  ]
};

const formats = [
  'size',
  'bold',
  'italic',
  'underline',
  'color',
  'align',
  'list',
  'blockquote',
  'link'
];

// Add custom CSS for dark mode and editor sizing
const customEditorStyles = `
  .dark .ql-toolbar .ql-stroke {
    stroke: #fff;
  }
  .dark .ql-toolbar .ql-fill {
    fill: #fff;
  }
  .dark .ql-toolbar .ql-picker {
    color: #fff;
  }
  .dark .ql-toolbar .ql-picker-options {
    background-color: #374151;
  }
  .dark .ql-container {
    border-color: #4B5563;
  }
  .dark .ql-editor {
    color: #fff;
  }
  .ql-container {
    height: auto !important;
    min-height: 150px;
  }
  .ql-editor {
    height: auto !important;
    min-height: 150px;
    max-height: 500px;
    overflow-y: auto;
  }
`;

const CreateBook = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [book, setBook] = useState({
    title: '',
    description: '',
    price: 0,
    status: 'draft',
    author: '',
    publisher: '',
    category_id: ''
  });
  const [coverPreview, setCoverPreview] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [bookFile, setBookFile] = useState(null);
  const [categories, setCategories] = useState([]);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '' });

  useEffect(() => {
    // جلب التصنيفات
    const fetchCategories = async () => {
      try {
        const response = await axiosInstance.get('/api/book-categories');
        setCategories(response.data);
      } catch (error) {
        console.error('Error fetching categories:', error);
        toastService.error('حدث خطأ أثناء جلب التصنيفات');
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = customEditorStyles;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // تحقق من صحة البيانات
      if (!book.title.trim()) {
        throw new Error('عنوان الكتاب مطلوب');
      }
      if (!book.description.trim()) {
        throw new Error('وصف الكتاب مطلوب');
      }
      if (!coverFile) {
        throw new Error('صورة الغلاف مطلوبة');
      }
      if (!bookFile) {
        throw new Error('ملف الكتاب مطلوب');
      }
      if (!book.category_id) {
        throw new Error('يجب اختيار تصنيف');
      }

      const formData = new FormData();
      formData.append('title', book.title.trim());
      formData.append('description', book.description.trim());
      formData.append('price', String(book.price));
      formData.append('status', book.status);
      formData.append('author', book.author);
      formData.append('publisher', book.publisher);
      formData.append('cover', coverFile);
      formData.append('file', bookFile);
      formData.append('category_id', book.category_id);

      await bookService.createBook(formData);
      toastService.success('تم إضافة الكتاب بنجاح');
      navigate('/admin/dashboard/books');
    } catch (error) {
      console.error('Error submitting book:', error);
      toastService.error(error.message || 'حدث خطأ أثناء حفظ الكتاب');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setBook(prev => ({
      ...prev,
      [name]: name === 'price' ? parseFloat(value) || 0 : value
    }));
  };

  const handleCoverChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // تحقق من نوع الملف
      if (!file.type.startsWith('image/')) {
        toastService.error('الرجاء اختيار ملف صورة صالح');
        return;
      }

      // حفظ الملف
      setCoverFile(file);

      // عرض المعاينة
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBookFile(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      if (type === 'cover') {
        handleCoverChange({ target: { files: [files[0]] } });
      } else if (type === 'book') {
        handleFileChange({ target: { files: [files[0]] } });
      }
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    try {
      await axiosInstance.delete(`/api/book-categories/${categoryId}`);
      setCategories(prev => prev.filter(category => category.id !== categoryId));
      toastService.success('تم حذف التصنيف بنجاح');
    } catch (error) {
      console.error('Error deleting category:', error);
      toastService.error('حدث خطأ أثناء حذف التصنيف');
    }
  };

  // إضافة تصنيف جديد
  const handleAddCategory = async () => {
    try {
      const response = await axiosInstance.post('/api/book-categories', newCategory);
      setCategories([...categories, response.data]);
      setNewCategory({ name: '' });
      setShowCategoryModal(false);
      toastService.success('تم إضافة التصنيف بنجاح');
    } catch (error) {
      console.error('Error adding category:', error);
      toastService.error('حدث خطأ أثناء إضافة التصنيف');
    }
  };

  return (
    <div className="p-6">
      {/* رأس الصفحة */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h1 className="text-lg font-medium text-gray-900 dark:text-white">إضافة كتاب</h1>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate('/admin/dashboard/books')}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              title="إلغاء"
            >
              <HiOutlineX className="w-5 h-5" />
            </button>
            <button
              type="submit"
              disabled={loading}
              className="text-primary-600 hover:text-primary-700 dark:text-primary-500 dark:hover:text-primary-400 disabled:opacity-50"
              title="إضافة"
              onClick={handleSubmit}
            >
              <HiOutlineCheck className="w-5 h-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-3 gap-6">
            {/* معلومات الكتاب */}
            <div className="col-span-2">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white">معلومات الكتاب</h2>
                </div>
                <div className="p-6 space-y-4">
                  {/* Modal إضافة تصنيف */}
                  <FormModal
                    isOpen={showCategoryModal}
                    onClose={() => {
                      setShowCategoryModal(false);
                      setNewCategory({ name: '' });
                    }}
                    onSubmit={handleAddCategory}
                    title="إضافة تصنيف جديد"
                  >
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        اسم التصنيف
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={newCategory.name}
                        onChange={(e) => setNewCategory({ name: e.target.value })}
                        className="input-base w-full"
                        required
                      />
                    </div>
                  </FormModal>
                  <div>
                    <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      عنوان الكتاب
                    </label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      value={book.title}
                      onChange={handleChange}
                      className="input-base w-full"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">وصف الكتاب</label>
                    <div className="prose prose-sm sm:prose lg:prose-lg xl:prose-xl mx-auto">
                      <ReactQuill
                        theme="snow"
                        value={book.description}
                        onChange={(value) => setBook(prev => ({ ...prev, description: value }))}
                        modules={modules}
                        formats={formats}
                        className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                        placeholder="اكتب وصف الكتاب هنا..."
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="author" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      المؤلف
                    </label>
                    <input
                      type="text"
                      id="author"
                      name="author"
                      value={book.author}
                      onChange={handleChange}
                      className="input-base w-full"
                    />
                  </div>

                  <div>
                    <label htmlFor="publisher" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      الناشر
                    </label>
                    <input
                      type="text"
                      id="publisher"
                      name="publisher"
                      value={book.publisher}
                      onChange={handleChange}
                      className="input-base w-full"
                    />
                  </div>

                  <div>
                    <label htmlFor="category_id" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      التصنيف
                    </label>
                    <div className="flex items-center gap-2">
                      <select
                        id="category_id"
                        name="category_id"
                        value={book.category_id}
                        onChange={handleChange}
                        className="input-base w-full"
                      >
                        <option value="">اختر التصنيف</option>
                        {categories.map(category => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setShowCategoryModal(true)}
                        className="text-primary-600 hover:text-primary-700 dark:text-primary-500 dark:hover:text-primary-400"
                        title="إضافة تصنيف"
                      >
                        <HiOutlinePlus className="w-5 h-5" />
                      </button>
                      {book.category_id && (
                        <button
                          type="button"
                          onClick={() => handleDeleteCategory(book.category_id)}
                          className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                          title="حذف التصنيف"
                        >
                          <HiOutlineTrash className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="price" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        السعر
                      </label>
                      <input
                        type="number"
                        id="price"
                        name="price"
                        value={book.price}
                        onChange={handleChange}
                        min="0"
                        step="0.01"
                        className="input-base w-full"
                      />
                    </div>

                    <div>
                      <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        الحالة
                      </label>
                      <select
                        id="status"
                        name="status"
                        value={book.status}
                        onChange={handleChange}
                        className="input-base w-full"
                      >
                        <option value="draft">مسودة</option>
                        <option value="published">منشور</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* الملفات */}
            <div>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="p-6 space-y-6">
                  {/* صورة الغلاف */}
                  <div>
                    <label
                      htmlFor="cover"
                      className="relative block cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault();
                        document.getElementById('cover').click();
                      }}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, 'cover')}
                    >
                      <div className={`flex flex-col items-center p-4 border-2 border-dashed ${coverPreview ? 'border-0 p-0' : 'border-gray-300 dark:border-gray-600'} rounded-lg hover:border-primary-500 dark:hover:border-primary-500 transition-colors h-[150px]`}>
                        {coverPreview ? (
                          <div className="relative group w-full h-full">
                            <img
                              src={coverPreview}
                              alt="معاينة الغلاف"
                              className="w-full h-full object-cover rounded-lg"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                              <span className="text-white">تغيير صورة الغلاف</span>
                            </div>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setCoverPreview(null);
                                setCoverFile(null);
                                document.getElementById('cover').value = '';
                              }}
                              className="absolute -top-2 -right-2 bg-red-100 text-red-600 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                              title="حذف الصورة"
                            >
                              <HiOutlineX className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center justify-center w-full h-full">
                            <HiOutlinePlus className="h-8 w-8 text-gray-400" />
                            <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                              <span className="font-medium">صورة الغلاف</span>
                            </div>
                            <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                              <span className="text-primary-600 hover:text-primary-500 dark:text-primary-500 dark:hover:text-primary-400">
                                اختر صورة
                              </span>
                              <span className="pr-1">أو اسحب وأفلت</span>
                            </div>
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              PNG, JPG حتى 10MB
                            </p>
                          </div>
                        )}
                      </div>
                    </label>
                    <input
                      id="cover"
                      name="cover"
                      type="file"
                      accept="image/*"
                      className="sr-only"
                      onChange={handleCoverChange}
                      required
                    />
                  </div>

                  {/* ملف الكتاب */}
                  <div>
                    <label
                      htmlFor="file"
                      className="relative block cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault();
                        document.getElementById('file').click();
                      }}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, 'book')}
                    >
                      <div className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-primary-500 dark:hover:border-primary-500 transition-colors h-[150px]">
                        <HiOutlinePlus className="h-8 w-8 text-gray-400" />
                        <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                          <span className="font-medium">ملف الكتاب</span>
                        </div>
                        <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                          <span className="text-primary-600 hover:text-primary-500 dark:text-primary-500 dark:hover:text-primary-400">
                            اختر ملف
                          </span>
                          <span className="pr-1">أو اسحب وأفلت</span>
                        </div>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          PDF فقط، حتى 50MB
                        </p>
                      </div>
                    </label>
                    <input
                      type="file"
                      id="file"
                      name="file"
                      accept=".pdf"
                      className="sr-only"
                      onChange={handleFileChange}
                      required
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateBook;
